/**
 * Client-side PDF listing flyer generator
 * Creates a professional downloadable PDF flyer with property photos and information
 * 
 * Features:
 * - Professional A4/Letter layout with modern design
 * - Multiple photos beautifully arranged
 * - Property details prominently displayed
 * - Multi-language support with proper Unicode rendering
 * - Dynamic text sizing to fit content
 * - Canvas-based PNG preview generation
 */

import { jsPDF } from 'jspdf';
import { DescriptionLanguage } from '@/types/property';

// Translations for flyer labels
const flyerTranslations: Record<string, Record<string, string>> = {
  en: {
    forSale: 'FOR SALE',
    forRent: 'FOR RENT',
    property: 'Property',
    bedrooms: 'Bedrooms',
    bedroom: 'Bedroom',
    bathrooms: 'Bathrooms',
    bathroom: 'Bathroom',
    size: 'Size',
    keyFeatures: 'Key Features',
    contact: 'Contact Us',
    perMonth: '/month',
    generatedWith: 'Generated with ListBright',
    aboutProperty: 'About This Property',
    location: 'Location',
    details: 'Property Details',
  },
  el: {
    forSale: 'ΠΩΛΕΙΤΑΙ',
    forRent: 'ΕΝΟΙΚΙΑΖΕΤΑΙ',
    property: 'Ακίνητο',
    bedrooms: 'Υπνοδωμάτια',
    bedroom: 'Υπνοδωμάτιο',
    bathrooms: 'Μπάνια',
    bathroom: 'Μπάνιο',
    size: 'Εμβαδόν',
    keyFeatures: 'Χαρακτηριστικά',
    contact: 'Επικοινωνία',
    perMonth: '/μήνα',
    generatedWith: 'Δημιουργήθηκε με ListBright',
    aboutProperty: 'Σχετικά με το ακίνητο',
    location: 'Τοποθεσία',
    details: 'Στοιχεία Ακινήτου',
  },
  de: {
    forSale: 'ZU VERKAUFEN',
    forRent: 'ZU VERMIETEN',
    property: 'Immobilie',
    bedrooms: 'Schlafzimmer',
    bedroom: 'Schlafzimmer',
    bathrooms: 'Badezimmer',
    bathroom: 'Badezimmer',
    size: 'Größe',
    keyFeatures: 'Ausstattung',
    contact: 'Kontakt',
    perMonth: '/Monat',
    generatedWith: 'Erstellt mit ListBright',
    aboutProperty: 'Über diese Immobilie',
    location: 'Standort',
    details: 'Objektdetails',
  },
  es: {
    forSale: 'EN VENTA',
    forRent: 'EN ALQUILER',
    property: 'Propiedad',
    bedrooms: 'Dormitorios',
    bedroom: 'Dormitorio',
    bathrooms: 'Baños',
    bathroom: 'Baño',
    size: 'Tamaño',
    keyFeatures: 'Características',
    contact: 'Contacto',
    perMonth: '/mes',
    generatedWith: 'Generado con ListBright',
    aboutProperty: 'Acerca de esta propiedad',
    location: 'Ubicación',
    details: 'Detalles de la propiedad',
  },
  fr: {
    forSale: 'À VENDRE',
    forRent: 'À LOUER',
    property: 'Propriété',
    bedrooms: 'Chambres',
    bedroom: 'Chambre',
    bathrooms: 'Salles de bain',
    bathroom: 'Salle de bain',
    size: 'Surface',
    keyFeatures: 'Caractéristiques',
    contact: 'Contact',
    perMonth: '/mois',
    generatedWith: 'Généré avec ListBright',
    aboutProperty: 'À propos de cette propriété',
    location: 'Emplacement',
    details: 'Détails du bien',
  },
  it: {
    forSale: 'IN VENDITA',
    forRent: 'IN AFFITTO',
    property: 'Proprietà',
    bedrooms: 'Camere da letto',
    bedroom: 'Camera da letto',
    bathrooms: 'Bagni',
    bathroom: 'Bagno',
    size: 'Superficie',
    keyFeatures: 'Caratteristiche',
    contact: 'Contatto',
    perMonth: '/mese',
    generatedWith: 'Generato con ListBright',
    aboutProperty: 'Informazioni sulla proprietà',
    location: 'Posizione',
    details: 'Dettagli proprietà',
  },
  ru: {
    forSale: 'ПРОДАЖА',
    forRent: 'АРЕНДА',
    property: 'Недвижимость',
    bedrooms: 'Спальни',
    bedroom: 'Спальня',
    bathrooms: 'Ванные',
    bathroom: 'Ванная',
    size: 'Площадь',
    keyFeatures: 'Особенности',
    contact: 'Контакты',
    perMonth: '/мес',
    generatedWith: 'Создано с ListBright',
    aboutProperty: 'Об этой недвижимости',
    location: 'Расположение',
    details: 'Детали недвижимости',
  },
  he: {
    forSale: 'למכירה',
    forRent: 'להשכרה',
    property: 'נכס',
    bedrooms: 'חדרי שינה',
    bedroom: 'חדר שינה',
    bathrooms: 'חדרי אמבטיה',
    bathroom: 'חדר אמבטיה',
    size: 'גודל',
    keyFeatures: 'תכונות',
    contact: 'יצירת קשר',
    perMonth: '/חודש',
    generatedWith: 'נוצר עם ListBright',
    aboutProperty: 'אודות הנכס',
    location: 'מיקום',
    details: 'פרטי הנכס',
  },
  ar: {
    forSale: 'للبيع',
    forRent: 'للإيجار',
    property: 'عقار',
    bedrooms: 'غرف نوم',
    bedroom: 'غرفة نوم',
    bathrooms: 'حمامات',
    bathroom: 'حمام',
    size: 'المساحة',
    keyFeatures: 'المميزات',
    contact: 'اتصل بنا',
    perMonth: '/شهر',
    generatedWith: 'تم الإنشاء بواسطة ListBright',
    aboutProperty: 'عن هذا العقار',
    location: 'الموقع',
    details: 'تفاصيل العقار',
  },
  pt: {
    forSale: 'À VENDA',
    forRent: 'PARA ALUGAR',
    property: 'Imóvel',
    bedrooms: 'Quartos',
    bedroom: 'Quarto',
    bathrooms: 'Banheiros',
    bathroom: 'Banheiro',
    size: 'Área',
    keyFeatures: 'Características',
    contact: 'Contato',
    perMonth: '/mês',
    generatedWith: 'Gerado com ListBright',
    aboutProperty: 'Sobre este imóvel',
    location: 'Localização',
    details: 'Detalhes do imóvel',
  },
  nl: {
    forSale: 'TE KOOP',
    forRent: 'TE HUUR',
    property: 'Woning',
    bedrooms: 'Slaapkamers',
    bedroom: 'Slaapkamer',
    bathrooms: 'Badkamers',
    bathroom: 'Badkamer',
    size: 'Oppervlakte',
    keyFeatures: 'Kenmerken',
    contact: 'Contact',
    perMonth: '/maand',
    generatedWith: 'Gemaakt met ListBright',
    aboutProperty: 'Over dit pand',
    location: 'Locatie',
    details: 'Woningdetails',
  },
  pl: {
    forSale: 'NA SPRZEDAŻ',
    forRent: 'DO WYNAJĘCIA',
    property: 'Nieruchomość',
    bedrooms: 'Sypialnie',
    bedroom: 'Sypialnia',
    bathrooms: 'Łazienki',
    bathroom: 'Łazienka',
    size: 'Powierzchnia',
    keyFeatures: 'Cechy',
    contact: 'Kontakt',
    perMonth: '/miesiąc',
    generatedWith: 'Wygenerowano z ListBright',
    aboutProperty: 'O nieruchomości',
    location: 'Lokalizacja',
    details: 'Szczegóły nieruchomości',
  },
  tr: {
    forSale: 'SATILIK',
    forRent: 'KİRALIK',
    property: 'Mülk',
    bedrooms: 'Yatak Odası',
    bedroom: 'Yatak Odası',
    bathrooms: 'Banyo',
    bathroom: 'Banyo',
    size: 'Büyüklük',
    keyFeatures: 'Özellikler',
    contact: 'İletişim',
    perMonth: '/ay',
    generatedWith: 'ListBright ile oluşturuldu',
    aboutProperty: 'Bu mülk hakkında',
    location: 'Konum',
    details: 'Mülk Detayları',
  },
  zh: {
    forSale: '出售',
    forRent: '出租',
    property: '房产',
    bedrooms: '卧室',
    bedroom: '卧室',
    bathrooms: '浴室',
    bathroom: '浴室',
    size: '面积',
    keyFeatures: '特点',
    contact: '联系方式',
    perMonth: '/月',
    generatedWith: '由ListBright生成',
    aboutProperty: '关于此房产',
    location: '位置',
    details: '房产详情',
  },
};

function getTranslation(language: DescriptionLanguage | undefined, key: string): string {
  const lang = language || 'en';
  return flyerTranslations[lang]?.[key] || flyerTranslations['en'][key] || key;
}

export interface FlyerOptions {
  photos: string[];
  propertyType?: string;
  city?: string;
  address?: string;
  price?: string;
  bedrooms?: number;
  bathrooms?: number;
  squareMeters?: string;
  listingType?: 'sale' | 'rent';
  features?: string[];
  description?: string;
  agentName?: string;
  agentPhone?: string;
  agentEmail?: string;
  agencyName?: string;
  language?: DescriptionLanguage;
  /** ISO currency code (USD, AED, EUR…). Defaults to USD for legacy callers. */
  currency?: string;
  /** 'sqm' or 'sqft'. Defaults to sqm for legacy callers. */
  areaUnit?: 'sqm' | 'sqft';
}

export interface FlyerResult {
  success: boolean;
  pdfUrl?: string;
  imagePreviewUrl?: string;
  blob?: Blob;
  imageBlob?: Blob;
  error?: string;
}

async function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

async function imageToBase64(src: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

/**
 * Generate a canvas-based PNG preview for display in browser
 */
export async function generateFlyerPreview(options: FlyerOptions): Promise<{ url: string; blob: Blob } | null> {
  const {
    photos,
    propertyType,
    city,
    address,
    price,
    bedrooms,
    bathrooms,
    squareMeters,
    listingType = 'sale',
    features = [],
    description,
    agentName,
    agentPhone,
    agencyName,
    language,
    currency = 'USD',
    areaUnit = 'sqm',
  } = options;

  const t = (key: string) => getTranslation(language, key);

  if (!photos || photos.length === 0) return null;

  try {
    // Create high-resolution canvas for preview
    const scale = 2;
    const width = 595 * scale; // A4 width at 72 DPI * scale
    const height = 842 * scale; // A4 height at 72 DPI * scale
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Color palette
    const colors = {
      primary: '#1a2530',
      accent: '#c8a05a',
      white: '#ffffff',
      lightBg: '#f8f9fa',
      text: '#2d2d30',
      textMuted: '#6e7580',
      success: '#28a064',
    };

    // Background
    ctx.fillStyle = colors.white;
    ctx.fillRect(0, 0, width, height);

    // Top accent bar
    ctx.fillStyle = colors.accent;
    ctx.fillRect(0, 0, width, 8 * scale);

    // Load first image for hero
    const heroHeight = 380 * scale;
    const margin = 24 * scale;
    
    try {
      const heroImg = await loadImage(photos[0]);
      const imgAspect = heroImg.width / heroImg.height;
      const heroWidth = width - margin * 2;
      
      let drawWidth = heroWidth;
      let drawHeight = heroWidth / imgAspect;
      
      if (drawHeight > heroHeight) {
        drawHeight = heroHeight;
        drawWidth = heroHeight * imgAspect;
      }
      
      const x = margin + (heroWidth - drawWidth) / 2;
      const y = 44 * scale;
      
      // Draw hero image with rounded corners (clip)
      ctx.save();
      ctx.beginPath();
      ctx.roundRect(margin, y, heroWidth, heroHeight, 8 * scale);
      ctx.clip();
      ctx.drawImage(heroImg, x, y, drawWidth, drawHeight);
      ctx.restore();
      
      // Gradient overlay on hero for text readability
      const gradient = ctx.createLinearGradient(0, y + heroHeight - 120 * scale, 0, y + heroHeight);
      gradient.addColorStop(0, 'rgba(0,0,0,0)');
      gradient.addColorStop(1, 'rgba(0,0,0,0.7)');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.roundRect(margin, y, heroWidth, heroHeight, 8 * scale);
      ctx.fill();

      // Status badge on hero
      const badgeText = listingType === 'rent' ? t('forRent') : t('forSale');
      ctx.font = `bold ${11 * scale}px Arial, sans-serif`;
      const badgeWidth = ctx.measureText(badgeText).width + 20 * scale;
      ctx.fillStyle = colors.accent;
      ctx.beginPath();
      ctx.roundRect(margin + 12 * scale, y + 12 * scale, badgeWidth, 24 * scale, 4 * scale);
      ctx.fill();
      ctx.fillStyle = colors.primary;
      ctx.textAlign = 'center';
      ctx.fillText(badgeText, margin + 12 * scale + badgeWidth / 2, y + 28 * scale);

      // Price on hero
      if (price) {
        const priceNum = parseInt(price);
        let priceStr: string;
        try {
          priceStr = new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 0 }).format(priceNum);
        } catch {
          priceStr = `${currency} ${priceNum.toLocaleString()}`;
        }
        const formattedPrice = `${priceStr}${listingType === 'rent' ? t('perMonth') : ''}`;
        ctx.font = `bold ${36 * scale}px Arial, sans-serif`;
        ctx.fillStyle = colors.white;
        ctx.textAlign = 'left';
        ctx.fillText(formattedPrice, margin + 16 * scale, y + heroHeight - 50 * scale);

        // Location under price
        if (city || address) {
          const locationText = [address, city].filter(Boolean).join(', ');
          ctx.font = `${14 * scale}px Arial, sans-serif`;
          ctx.fillStyle = 'rgba(255,255,255,0.9)';
          ctx.fillText(locationText, margin + 16 * scale, y + heroHeight - 20 * scale);
        }
      }
    } catch (err) {
      console.warn('Failed to load hero image for preview');
    }

    let currentY = 44 * scale + heroHeight + 20 * scale;

    // Property stats bar
    const stats: Array<{ value: string; label: string; icon: string }> = [];
    if (bedrooms !== undefined) {
      stats.push({ value: String(bedrooms), label: bedrooms === 1 ? t('bedroom') : t('bedrooms'), icon: '🛏️' });
    }
    if (bathrooms !== undefined) {
      stats.push({ value: String(bathrooms), label: bathrooms === 1 ? t('bathroom') : t('bathrooms'), icon: '🚿' });
    }
    if (squareMeters) {
      const sqmNum = parseFloat(squareMeters);
      const areaValue = isFinite(sqmNum)
        ? (areaUnit === 'sqft'
            ? `${Math.round(sqmNum * 10.7639).toLocaleString()} sqft`
            : `${Math.round(sqmNum).toLocaleString()} m²`)
        : `${squareMeters} ${areaUnit === 'sqft' ? 'sqft' : 'm²'}`;
      stats.push({ value: areaValue, label: t('size'), icon: '📐' });
    }
    if (propertyType) {
      stats.push({ value: propertyType.charAt(0).toUpperCase() + propertyType.slice(1), label: t('property'), icon: '🏠' });
    }

    if (stats.length > 0) {
      const statsHeight = 50 * scale;
      ctx.fillStyle = colors.lightBg;
      ctx.beginPath();
      ctx.roundRect(margin, currentY, width - margin * 2, statsHeight, 8 * scale);
      ctx.fill();

      const statWidth = (width - margin * 2) / stats.length;
      stats.forEach((stat, i) => {
        const xCenter = margin + statWidth * i + statWidth / 2;
        
        // Icon and value
        ctx.font = `bold ${18 * scale}px Arial, sans-serif`;
        ctx.fillStyle = colors.primary;
        ctx.textAlign = 'center';
        ctx.fillText(`${stat.icon} ${stat.value}`, xCenter, currentY + 22 * scale);
        
        // Label
        ctx.font = `${10 * scale}px Arial, sans-serif`;
        ctx.fillStyle = colors.textMuted;
        ctx.fillText(stat.label.toUpperCase(), xCenter, currentY + 38 * scale);
        
        // Divider
        if (i < stats.length - 1) {
          ctx.strokeStyle = colors.textMuted;
          ctx.lineWidth = 0.5 * scale;
          ctx.beginPath();
          ctx.moveTo(margin + statWidth * (i + 1), currentY + 10 * scale);
          ctx.lineTo(margin + statWidth * (i + 1), currentY + statsHeight - 10 * scale);
          ctx.stroke();
        }
      });

      currentY += statsHeight + 16 * scale;
    }

    // Photo grid (up to 4 secondary photos)
    if (photos.length > 1) {
      const gridPhotos = photos.slice(1, 5);
      const photoGap = 6 * scale;
      const cols = Math.min(gridPhotos.length, 4);
      const photoWidth = (width - margin * 2 - photoGap * (cols - 1)) / cols;
      const photoHeight = 70 * scale;

      for (let i = 0; i < gridPhotos.length; i++) {
        try {
          const img = await loadImage(gridPhotos[i]);
          const xPos = margin + i * (photoWidth + photoGap);
          
          ctx.save();
          ctx.beginPath();
          ctx.roundRect(xPos, currentY, photoWidth, photoHeight, 4 * scale);
          ctx.clip();
          
          // Cover fit
          const imgAspect = img.width / img.height;
          const boxAspect = photoWidth / photoHeight;
          let dw, dh, dx, dy;
          if (imgAspect > boxAspect) {
            dh = photoHeight;
            dw = dh * imgAspect;
            dx = xPos - (dw - photoWidth) / 2;
            dy = currentY;
          } else {
            dw = photoWidth;
            dh = dw / imgAspect;
            dx = xPos;
            dy = currentY - (dh - photoHeight) / 2;
          }
          ctx.drawImage(img, dx, dy, dw, dh);
          ctx.restore();
        } catch (err) {
          console.warn(`Failed to load grid photo ${i}`);
        }
      }

      currentY += photoHeight + 16 * scale;
    }

    // Description section
    if (description) {
      // Section header
      ctx.fillStyle = colors.accent;
      ctx.fillRect(margin, currentY, 4 * scale, 16 * scale);
      
      ctx.font = `bold ${12 * scale}px Arial, sans-serif`;
      ctx.fillStyle = colors.primary;
      ctx.textAlign = 'left';
      ctx.fillText(t('aboutProperty'), margin + 12 * scale, currentY + 12 * scale);
      currentY += 24 * scale;

      // Description text - dynamically sized
      const maxDescLength = 300;
      const truncatedDesc = description.length > maxDescLength 
        ? description.substring(0, maxDescLength).trim() + '...' 
        : description;
      
      ctx.font = `${11 * scale}px Arial, sans-serif`;
      ctx.fillStyle = colors.text;
      
      // Word wrap
      const maxWidth = width - margin * 2;
      const words = truncatedDesc.split(' ');
      let line = '';
      let lineCount = 0;
      const maxLines = 4;
      
      for (let i = 0; i < words.length && lineCount < maxLines; i++) {
        const testLine = line + words[i] + ' ';
        const testWidth = ctx.measureText(testLine).width;
        
        if (testWidth > maxWidth && line !== '') {
          ctx.fillText(line.trim(), margin, currentY);
          currentY += 14 * scale;
          lineCount++;
          line = words[i] + ' ';
        } else {
          line = testLine;
        }
      }
      if (line && lineCount < maxLines) {
        ctx.fillText(line.trim(), margin, currentY);
        currentY += 14 * scale;
      }
      
      currentY += 8 * scale;
    }

    // Features section
    if (features.length > 0) {
      ctx.fillStyle = colors.accent;
      ctx.fillRect(margin, currentY, 4 * scale, 16 * scale);
      
      ctx.font = `bold ${12 * scale}px Arial, sans-serif`;
      ctx.fillStyle = colors.primary;
      ctx.textAlign = 'left';
      ctx.fillText(t('keyFeatures'), margin + 12 * scale, currentY + 12 * scale);
      currentY += 24 * scale;

      const displayFeatures = features.slice(0, 6);
      const cols = 2;
      const colWidth = (width - margin * 2) / cols;
      
      displayFeatures.forEach((feature, i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        const xPos = margin + col * colWidth;
        const yPos = currentY + row * 16 * scale;
        
        ctx.font = `${11 * scale}px Arial, sans-serif`;
        ctx.fillStyle = colors.success;
        ctx.textAlign = 'left';
        ctx.fillText('✓', xPos, yPos);
        ctx.fillStyle = colors.text;
        ctx.fillText(feature, xPos + 12 * scale, yPos);
      });
      
      currentY += Math.ceil(displayFeatures.length / cols) * 16 * scale + 12 * scale;
    }

    // Contact section
    if (agentName || agentPhone || agencyName) {
      const contactHeight = 48 * scale;
      ctx.fillStyle = colors.primary;
      ctx.beginPath();
      ctx.roundRect(margin, currentY, width - margin * 2, contactHeight, 6 * scale);
      ctx.fill();

      ctx.fillStyle = colors.white;
      ctx.font = `${9 * scale}px Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.fillText(t('contact').toUpperCase(), margin + 12 * scale, currentY + 14 * scale);

      let contactText = '';
      if (agentName) contactText += agentName;
      if (agentPhone) contactText += (contactText ? '  •  ' : '') + agentPhone;
      if (agencyName) contactText += (contactText ? '  •  ' : '') + agencyName;
      
      ctx.font = `bold ${12 * scale}px Arial, sans-serif`;
      ctx.fillText(contactText, margin + 12 * scale, currentY + 34 * scale);
    }

    // Footer
    ctx.fillStyle = colors.accent;
    ctx.fillRect(0, height - 20 * scale, width, 20 * scale);
    ctx.font = `${8 * scale}px Arial, sans-serif`;
    ctx.fillStyle = colors.primary;
    ctx.textAlign = 'center';
    ctx.fillText(t('generatedWith'), width / 2, height - 7 * scale);

    // Convert to blob
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve({ url: URL.createObjectURL(blob), blob });
        } else {
          resolve(null);
        }
      }, 'image/png', 0.95);
    });
  } catch (error) {
    console.error('Preview generation error:', error);
    return null;
  }
}

/**
 * Generate a professional PDF listing flyer with modern design
 * Uses canvas-based rendering to support all Unicode characters (Greek, Cyrillic, Chinese, Arabic, etc.)
 */
export async function generateListingFlyer(options: FlyerOptions): Promise<FlyerResult> {
  const {
    photos,
    propertyType,
    city,
    address,
    price,
    bedrooms,
    bathrooms,
    squareMeters,
    listingType = 'sale',
    features = [],
    description,
    agentName,
    agentPhone,
    agentEmail,
    agencyName,
    language,
  } = options;

  const t = (key: string) => getTranslation(language, key);

  if (!photos || photos.length === 0) {
    return { success: false, error: 'No photos provided' };
  }

  try {
    console.log('📄 Generating professional PDF listing flyer with Unicode support...');
    
    // Generate the flyer as a high-resolution canvas image first
    // This ensures proper rendering of all Unicode characters (Greek, Cyrillic, Chinese, Arabic, etc.)
    const previewResult = await generateFlyerPreview(options);
    if (!previewResult) {
      return { success: false, error: 'Failed to generate flyer preview' };
    }

    // Create PDF (A4 size) and embed the canvas-rendered image
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = 210;
    const pageHeight = 297;

    // Add the canvas-rendered image to fill the entire PDF page
    // This preserves all Unicode characters correctly
    pdf.addImage(previewResult.url, 'PNG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');

    // Generate PDF blob
    const pdfBlob = pdf.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);

    console.log('✅ PDF listing flyer generated successfully with full Unicode support');
    return { 
      success: true, 
      pdfUrl, 
      imagePreviewUrl: previewResult.url, 
      blob: pdfBlob, 
      imageBlob: previewResult.blob 
    };

  } catch (error) {
    console.error('Flyer generation error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

// Helper to download the flyer
export function downloadFlyer(blob: Blob, filename: string = 'property-flyer.pdf') {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
